<template>
    <div>
        <div v-if="disabled" class="disabled-overlay"></div>
        <GmapMap ref="gmap" :options="mapOptions" :center="center" :zoom="zoom" :style="pickerStyle"
            @click="handleMapClick">
            <GmapMarker v-if="markerPosition" :position="markerPosition" :options="markerOptions" />
            <GmapMarker v-if="startMarker" :position="startMarker" :options="startMarkerOptions" />
            <GmapMarker v-if="endMarker" :position="endMarker" :options="endMarkerOptions" />
        </GmapMap>
    </div>
</template>

<script>
import axios from 'axios';
export default {
    props: ['height', 'disabled', 'start', 'end'],
    data() {
        return {
            center: { lat: 13.746859146583096, lng: 100.53275575186706 },
            zoom: 18,
            markerPosition: { lat: 0, lng: 0 },
            address: '',
            pickerHeight: '90vh',
            startMarker: null,
            endMarker: null,
            startMarkerIcon: {
                url: '/svg/shopMarker.png',
                scaledSize: { width: 70, height: 70 },
            },
            endMarkerIcon: {
                url: '/svg/destination.png',
                scaledSize: { width: 70, height: 70 },
            },
            mapStyles: [
                {
                    featureType: 'road',
                    elementType: 'geometry',
                    stylers: [{ visibility: 'simplified' }]
                },
                {
                    featureType: 'poi',
                    elementType: 'all',
                    stylers: [{ visibility: 'off' }]
                },
            ],
        };
    },
    created() {
        if (this.height) {
            this.pickerHeight = this.height;
        }
        if (this.start) {
            this.startMarker = { lat: this.start.lat, lng: this.start.lng };
        }
        if (this.end) {
            this.endMarker = { lat: this.end.lat, lng: this.end.lng };
        }
    },
    watch: {
        start(newStart) {
            this.startMarker = { lat: newStart.lat, lng: newStart.lng };
            this.adjustMapBounds();
        },
        end(newEnd) {
            this.endMarker = { lat: newEnd.lat, lng: newEnd.lng };
            this.adjustMapBounds();
        }
    },
    computed: {
        pickerStyle() {
            return {
                width: '100%',
                height: this.pickerHeight,
                transition: 'height 0.5s ease'
            };
        },
        mapOptions() {
            return {
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: false,
                draggable: !this.disabled,
                disableDoubleClickZoom: this.disabled,
                scrollwheel: !this.disabled,
                styles: this.mapStyles
            };
        },
        markerOptions() {
            return {
                draggable: !this.disabled,
            };
        },
        startMarkerOptions() {
            return {
                icon: this.startMarkerIcon,
            };
        },
        endMarkerOptions() {
            return {
                icon: this.endMarkerIcon,
            };
        },
    },
    methods: {
        loadGoogleMapsApi() {
            if (typeof google === 'undefined') {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&callback=initMap`;
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);
                script.onload = () => {
                    console.log('Google Maps API loaded');
                };
            }
        },
        getLatLong() {
            return this.markerPosition;
        },
        setPickerHeight(value) {
            this.pickerHeight = value;
            this.center = this.markerPosition;
        },
        handleMapClick(event) {
            if (this.disabled) return;
            this.markerPosition = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            };
            this.getAddress(this.markerPosition.lat, this.markerPosition.lng);
        },
        handleMarkerDrag(event) {
            if (this.disabled) return;
            this.markerPosition = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            };
            this.getAddress(this.markerPosition.lat, this.markerPosition.lng);
        },
        setCurrentLocation(position) {
            const { latitude, longitude } = position.coords
            this.center = { lat: latitude, lng: longitude };
            this.markerPosition = { lat: latitude, lng: longitude };
            this.getAddress(latitude, longitude);
        },
        setDefaultLocation() {
            this.markerPosition = {
                lat: 13.746859146583096,
                lng: 100.53275575186706
            };
            this.center = this.markerPosition;
            console.log("Set Default Location");
        },
        getUserLocation() {
            if (typeof navigator.geolocation !== 'undefined') {
                navigator.geolocation.getCurrentPosition(this.setCurrentLocation, this.setDefaultLocation);
            } else {
                console.error('Geolocation is not supported by this browser.');
            }
        },
        async getAddress(lat, lng) {
            if (typeof google !== 'undefined') {
                const geocoder = new google.maps.Geocoder();
                const latlng = { lat, lng };

                geocoder.geocode({ location: latlng }, (results, status) => {
                    if (status === 'OK') {
                        if (results[0]) {
                            this.address = results[0].formatted_address;
                            this.$emit('address-updated', this.address); // Emit the address
                        } else {
                            console.log('No results found');
                            this.address = 'No address found';
                        }
                    } else {
                        console.log('Geocoder failed due to: ' + status);
                        this.address = 'Geocoder failed';
                    }
                });
            } else {
                console.error('Google Maps JavaScript API not loaded.')
                this.loadGoogleMapsApi()
                window.reload()
            }
        },
        async adjustMapBounds() {
            if (this.startMarker && this.endMarker) {
                if (typeof google !== 'undefined') {
                    try {
                        const bounds = new google.maps.LatLngBounds();
                        bounds.extend(this.startMarker);
                        bounds.extend(this.endMarker);
                        this.$refs.gmap.$mapObject.fitBounds(bounds);
                    } catch (error) {
                        console.error(error);
                    }
                } else {
                    console.error('Google Maps JavaScript API not loaded.');
                    this.loadGoogleMapsApi()
                    window.reload()
                }
            }
        },
        async calculateDistance() {
            if (!this.startMarker || !this.endMarker) return 0;
            const response = await axios.get(`http://localhost:3000/api/distancematrix`, {
                params: {
                    origins: `${this.startMarker.lat},${this.startMarker.lng}`,
                    destinations: `${this.endMarker.lat},${this.endMarker.lng}`,
                    key: 'AIzaSyCd7wY6t5DtOttGJieOWCaHkR5mYapj9yE',
                },
            });
            if (response.data.status === 'OK') {
                return response.data.rows[0].elements[0].distance
            } else {
                console.error('Error fetching distance data:', response.data.error_message);
            }
        },
    }
};
</script>

<style>
.disabled-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10;
    cursor: not-allowed;
}

.gm-style .gm-style-iw-tc {
    visibility: hidden;
}

.gm-style .gm-style-iw-c {
    visibility: hidden;
}
</style>
